import { orderBy } from 'lodash';
import { Maybe, ZonedDateTime } from '@csp/csp-common-model';
import { RequestWhileService } from '../service/RequestWhileService';
import { ScheduleIntervalService } from '../service/ScheduleIntervalService';
import { isWindowActiveNow } from '../util/scheduleUtil';
import { RolloutTimingResult } from '../timing/model/RolloutTimingResult';
import { TimingWindow } from '../timing/model/TimingWindow';
import { RequestWindow } from './RequestWindow';
import { TimingRepeatType } from './schedulingModels/TimingRepeatType';
import { GenericRequest } from './schedulingModels/GenericRequest';
import { ScheduleReferenceDates } from './ScheduleReferenceDates';
import { ScheduleVersionInfo } from './schedulingModels/ScheduleVersionInfo';
import { ScheduleWindowInterval } from './ScheduleWindowInterval';
import { RequestWindowValidity } from './RequestWindowValidity';

export type RolloutRequestResult = {
  startActionOccured: boolean;
  isRepeating: boolean;

  /**
   * If the request is currently active for the user based on while criteria.
   */
  isApplicableNow: boolean;

  requestWindows: RequestWindow[];
  hasLimitedRequestWindows?: boolean;
};

const from = (
  requestWindows: RequestWindow[],
  repeatType: TimingRepeatType,
  startActionOccured: boolean,
  isApplicableNow: boolean,
  hasLimitedRequestWindows?: boolean,
): RolloutRequestResult => ({
  startActionOccured,
  isRepeating: repeatType !== TimingRepeatType.NONE,
  isApplicableNow,
  requestWindows,
  hasLimitedRequestWindows,
});

const fromResults = (rolloutResults: RolloutRequestResult[]): RolloutRequestResult => {
  const allRequestWindows: RequestWindow[] = [];
  let isApplicableNow = false;
  let isRepeating = false;
  let startActionOccured = false;

  for (const result of rolloutResults) {
    allRequestWindows.push(...result.requestWindows);
    isApplicableNow = isApplicableNow || result.isApplicableNow;
    isRepeating = isRepeating || result.isRepeating;
    startActionOccured = startActionOccured || result.startActionOccured;
  }
  return {
    requestWindows: orderBy(allRequestWindows, requestWindow => requestWindow.windowStart.unixTimeMillis),
    isApplicableNow,
    isRepeating,
    startActionOccured,
  };
};

/** Helper function to map rollout timing to request result */
const fromTimingResult = (
  timingResult: RolloutTimingResult,
  request: GenericRequest,
  scheduleReferenceDates?: ScheduleReferenceDates,
  versionWindows?: ScheduleVersionInfo[],
  validBetween?: ScheduleWindowInterval,
  startActionSourceId?: string,
): RolloutRequestResult => {
  const applicableTimingWindows = timingResult.timingWindows.filter(timingWindow =>
    // If the consumer does not provide scheduleReferenceDates, we should not check applicability with while config
    scheduleReferenceDates
      ? RequestWhileService.isOverlappingWithMatchingInterval(request, scheduleReferenceDates, timingWindow)
      : true,
  );

  const applicableTimingWindowsWithinVersion = versionWindows
    ? applicableTimingWindows.filter(timingWindow => isWindowwWithinVersion(timingWindow, versionWindows))
    : applicableTimingWindows;

  const requestWindows = applicableTimingWindowsWithinVersion.map(timingWindow =>
    RequestWindow.from(
      request,
      timingWindow,
      isWindowInterrupted(request, timingWindow, scheduleReferenceDates),
      getWindowValidity(timingWindow, validBetween),
      versionWindows,
      startActionSourceId,
    ),
  );
  const isApplicableNow = scheduleReferenceDates
    ? RequestWhileService.isWithinMatchingIntervalNow(request, scheduleReferenceDates)
    : true;

  return RolloutRequestResult.from(requestWindows, timingResult.repeatType, true, isApplicableNow);
};

const isExclusiveTimestampWithinWindow = (window: ScheduleWindowInterval, timestamp: Maybe<ZonedDateTime>): boolean =>
  isWindowActiveNow(window, timestamp) || timestamp?.unixTimeMillis === window.windowEnd?.unixTimeMillis;

const getWindowValidity = (
  timingWindow: TimingWindow,
  validBetween: Maybe<ScheduleWindowInterval>,
): Maybe<RequestWindowValidity> => {
  if (!validBetween) {
    return undefined;
  }

  const overlapsBeginningOfValidBetween =
    !isWindowActiveNow(validBetween, timingWindow.windowStart) &&
    isExclusiveTimestampWithinWindow(validBetween, timingWindow.windowEnd) &&
    // Since the validBetween is based on the start action offset timestamp,
    // we don't want the first window to be invalid at any point if it stretches
    // backwards with windowBefore.
    timingWindow.repeatIndex !== 0;

  const overlappingEndOfValidBetween =
    !isExclusiveTimestampWithinWindow(validBetween, timingWindow.windowEnd) &&
    isWindowActiveNow(validBetween, timingWindow.windowStart);

  if (overlapsBeginningOfValidBetween || overlappingEndOfValidBetween) {
    return {
      validFrom: overlapsBeginningOfValidBetween ? validBetween.windowStart : undefined,
      validTo: overlappingEndOfValidBetween ? validBetween.windowEnd : undefined,
    };
  } else {
    return undefined;
  }
};

const isWindowwWithinVersion = (timingWindow: TimingWindow, versions: ScheduleVersionInfo[]): boolean =>
  versions.some(version =>
    ScheduleIntervalService.intersection(timingWindow, {
      windowStart: version.activeFrom,
      windowEnd: version.activeTo,
    }),
  );

const isWindowInterrupted = (
  request: GenericRequest,
  timing: TimingWindow,
  scheduleReferenceDates: Maybe<ScheduleReferenceDates>,
): boolean =>
  !!scheduleReferenceDates &&
  !RequestWhileService.isContainedWithinMatchingInterval(request, scheduleReferenceDates, timing);

export const RolloutRequestResult = { from, fromResults, fromTimingResult };
